import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { GEMINI_API_KEY } from '../../config/firebase';

export default function SupportWidget({ db, user }) {
    const [isOpen, setIsOpen] = useState(false);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [category, setCategory] = useState('General');
    const [submitted, setSubmitted] = useState(false);

    // AI State
    const [aiResponse, setAiResponse] = useState('');
    const [isThinking, setIsThinking] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;

        try {
            await addDoc(collection(db, "support_tickets"), {
                userId: user?.uid || 'guest',
                userName: user?.vendorName || user?.firstName || 'Guest',
                email: user?.email || 'Anonymous',
                subject: subject || 'No Subject',
                message,
                category,
                status: 'open',
                createdAt: serverTimestamp()
            });
            setSubmitted(true);
            setTimeout(() => {
                setSubmitted(false);
                setIsOpen(false);
                setMessage('');
                setSubject('');
                setAiResponse(''); // Clear AI response on submit
            }, 3000);
        } catch (error) {
            console.error("Support Error:", error);
            alert("Failed to send message.");
        }
    };

    // Handle AI Support Request
    const handleAskAI = async (e) => {
        e.preventDefault();
        if (!message.trim()) {
            alert("Please describe your issue first.");
            return;
        }
        setIsThinking(true);
        setAiResponse('');
        const apiKey = GEMINI_API_KEY; 

        try {
            const prompt = `You are a helpful support assistant for 'Host-it!', a peer-to-peer party rental marketplace connecting Vendors and Renters.
            
            Platform Context:
            - Vendors list party supplies (tents, tables, decor).
            - Renters book items securely via Stripe.
            - We have AI planning tools and E-vites.
            
            User's Issue:
            Category: ${category}
            Subject: ${subject}
            Message: ${message}
            
            Task:
            Provide a helpful, immediate answer or troubleshooting step. 
            If the issue likely requires human admin intervention (e.g. refund, ban report, database error), advise them to click "Send Message" to contact the team.
            Keep it concise and friendly.`;

            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents: [{ parts: [{ text: prompt }] }]
                })
            });
            
            const data = await response.json();
            const reply = data.candidates?.[0]?.content?.parts?.[0]?.text;
            if (reply) setAiResponse(reply);
        } catch (error) {
            console.error("AI Support Error:", error);
        } finally {
            setIsThinking(false);
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-[200] font-sans">
            {!isOpen && (
                <button 
                    onClick={() => setIsOpen(true)}
                    className="bg-pink-600 hover:bg-pink-700 text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
                >
                    <span className="text-2xl">?</span>
                </button>
            )}

            {isOpen && (
                <div className="bg-white rounded-2xl shadow-2xl w-80 sm:w-96 border border-gray-100 overflow-hidden animate-fade-in-up flex flex-col max-h-[80vh]">
                    <div className="bg-pink-600 p-4 flex justify-between items-center text-white flex-shrink-0">
                        <h3 className="font-bold">Contact Support</h3>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-pink-700 p-1 rounded">✕</button>
                    </div>
                    
                    <div className="p-6 overflow-y-auto">
                        {submitted ? (
                            <div className="text-center py-8">
                                <div className="text-5xl mb-4">✅</div>
                                <h4 className="font-bold text-gray-800">Message Sent!</h4>
                                <p className="text-gray-500 text-sm mt-2">We'll get back to you shortly.</p>
                            </div>
                        ) : (
                            <form className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Topic</label>
                                    <select 
                                        value={category} 
                                        onChange={(e) => setCategory(e.target.value)} 
                                        className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white transition"
                                    >
                                        <option>General Inquiry</option>
                                        <option>Billing Issue</option>
                                        <option>Technical Bug</option>
                                        <option>Report a User</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Subject</label>
                                    <input 
                                        type="text" 
                                        value={subject} 
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                                        placeholder="Brief summary..."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Message</label>
                                    <textarea 
                                        value={message} 
                                        onChange={(e) => setMessage(e.target.value)}
                                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none h-32 resize-none"
                                        placeholder="How can we help?"
                                        required
                                    />
                                </div>

                                {/* AI Response Area */}
                                {aiResponse && (
                                    <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 mb-4 animate-fade-in">
                                        <div className="flex items-center gap-2 mb-2 text-purple-700 font-bold text-xs uppercase">
                                            <span>✨</span> AI Assistant
                                        </div>
                                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{aiResponse}</p>
                                        <p className="text-xs text-gray-400 mt-2 italic">Not helpful? Click "Send Message" below to contact a human.</p>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <button 
                                        onClick={handleAskAI}
                                        disabled={isThinking}
                                        className="w-full bg-purple-100 text-purple-700 py-3 rounded-lg font-bold text-sm hover:bg-purple-200 transition flex items-center justify-center gap-2"
                                    >
                                        {isThinking ? (
                                            <>
                                                <span className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full"></span>
                                                Thinking...
                                            </>
                                        ) : (
                                            <>
                                                <span>✨</span> Get Instant AI Help
                                            </>
                                        )}
                                    </button>
                                    
                                    <button 
                                        onClick={handleSubmit} 
                                        className="w-full bg-gray-900 text-white py-3 rounded-lg font-bold text-sm hover:bg-gray-800 transition"
                                    >
                                        Send Message to Support
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}